// ── MeetingMap 이펙트 + API 함수 + 당겨서새로고침 훅 ─────────────────────────
// - resize / view 전환 / 날짜 변경 / 마운트 이펙트
// - fetchMeetings, fetchPendingFeedback, fetchContractTicketTypes
// - hasValiditySetting (헬퍼) · extractValidity·parseContractTicketString·외부 분류 바꾸기는 meetingMapTicketHelpers.js (2026-10-22 분리)
// - pull-to-refresh 핸들러

import { useEffect, useRef } from 'react';
import { apiClient } from '../../../api/apiClient';
import { MOBILE_BREAKPOINT, addDaysYmd } from '../../../components/meetingmap/utils';
import { resolveExternalProducts, extractValidity, parseContractTicketString } from './meetingMapTicketHelpers';
import { HQ_MEETING_API } from '../meetingMapApi';

// 마커 드래그 배치 저장 키 (user_preferences) — { 'YYYY-MM-DD': { [콜id]: { lat, lng } } }
const MARKER_LAYOUT_PREF_KEY = 'meeting_map_marker_positions';
const MARKER_LAYOUT_KEEP_DAYS = 30; // 저장 시 30일 지난 날짜 자동 정리

export function useMeetingMapHandlers(core, { adminMode, adminSalesName, showToast, refreshToken = 0, api = HQ_MEETING_API }) {
  const {
    view, visitDate, setLoading,
    setMeetings, setSelectedId, setSheetOpen, setIsMobile,
    setResultForm, setPendingList, setPendingLoading,
    contractTicketTypes, setContractTicketTypes,
    externalCategories, setExternalCategories,
    mapRef,
    shouldFitRef, draggedPositions, saveMarkerLayoutRef,
    baseDateRef,
  } = core;

  const saveLayoutTimerRef = useRef(null);

  // ── resize 이펙트 ──────────────────────────────────────────────────────────
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── view 전환 이펙트 ───────────────────────────────────────────────────────
  useEffect(() => {
    if (view === 'list') { setSheetOpen(false); return; }
    // map으로 전환 시 지도 크기 재계산 (숨겨진 상태에서 전환 시 타일 레이아웃 깨짐 방지)
    const timer = setTimeout(() => { if (mapRef.current) mapRef.current.invalidateSize(); }, 80);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  // ── 날짜 변경 → 미팅 목록 재조회 ───────────────────────────────────────────
  useEffect(() => {
    fetchMeetings(visitDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visitDate, adminMode, adminSalesName, api.base]);

  // ── 마운트 시 초기 데이터 조회 ─────────────────────────────────────────────
  // refreshToken: 관리자 ⚙️ 설정 모달을 닫으면 증가 → 인계·옵션 재조회 (2026-09-02 설정 즉시 반영)
  useEffect(() => {
    fetchPendingFeedback();
    fetchMeetingRefs();
    // 외부 카테고리 선제 로드 → 마커 색상이 모달 열기 전에도 올바르게 표시되어야 함
    fetchContractTicketTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminMode, adminSalesName, refreshToken, api.base]);

  // ── 마커 배치 영속화 (user_preferences) ─────────────────────────────────────
  // 서버에 날짜별로 저장 — 새로고침·메뉴 이동·기기 변경에도 배치 유지. 초기화는 전용 버튼으로만.
  async function loadMarkerLayout(ymd) {
    try {
      const r = await apiClient.get(`/api/my/preferences/${MARKER_LAYOUT_PREF_KEY}`);
      const positions = r.ok ? r.data?.value?.[ymd] : null;
      draggedPositions.current.clear();
      if (positions && typeof positions === 'object') {
        for (const [id, pos] of Object.entries(positions)) {
          const lat = Number(pos?.lat); const lng = Number(pos?.lng);
          if (Number.isFinite(lat) && Number.isFinite(lng)) {
            draggedPositions.current.set(Number(id), { lat, lng });
          }
        }
      }
    } catch (_) { /* 배치 로드 실패는 치명적이지 않음 — 원위치로 표시 */ }
  }

  async function saveMarkerLayoutNow() {
    try {
      const ymd = visitDate;
      const r = await apiClient.get(`/api/my/preferences/${MARKER_LAYOUT_PREF_KEY}`);
      const value = (r.ok && r.data?.value && typeof r.data.value === 'object') ? { ...r.data.value } : {};
      // 오래된 날짜 자동 정리 (KEEP_DAYS 이전)
      const cutoff = addDaysYmd(baseDateRef.current, -MARKER_LAYOUT_KEEP_DAYS);
      for (const d of Object.keys(value)) { if (d < cutoff) delete value[d]; }
      const positions = Object.fromEntries(
        [...draggedPositions.current.entries()].map(([id, pos]) => [String(id), pos])
      );
      if (Object.keys(positions).length > 0) value[ymd] = positions;
      else delete value[ymd];
      await apiClient.put(`/api/my/preferences/${MARKER_LAYOUT_PREF_KEY}`, { value });
    } catch (_) { /* 저장 실패는 다음 드래그에서 재시도됨 */ }
  }

  // dragend 마다 호출 — 연속 드래그를 묶어서 저장 (디바운스)
  function scheduleMarkerLayoutSave() {
    if (saveLayoutTimerRef.current) clearTimeout(saveLayoutTimerRef.current);
    saveLayoutTimerRef.current = setTimeout(saveMarkerLayoutNow, 800);
  }
  saveMarkerLayoutRef.current = scheduleMarkerLayoutSave;

  // 배치 초기화 — 이 날짜의 저장 배치 삭제 + 마커 원위치 (재조회로 반영)
  async function resetMarkerLayout() {
    if (saveLayoutTimerRef.current) clearTimeout(saveLayoutTimerRef.current);
    draggedPositions.current.clear();
    await saveMarkerLayoutNow(); // 빈 배치 → 해당 날짜 키 삭제
    await fetchMeetings(visitDate);
    showToast('마커 배치를 원래 위치로 되돌렸습니다.', 'success');
  }

  // ── API: 미팅 목록 조회 ─────────────────────────────────────────────────────
  async function fetchMeetings(ymd) {
    setLoading(true);
    shouldFitRef.current = true;
    // 저장된 배치 로드 (날짜별) — 마커 렌더 전에 draggedPositions 를 채워야 함
    await loadMarkerLayout(ymd);
    const query = new URLSearchParams({ visit_date: ymd });
    if (adminMode && String(adminSalesName || '').trim()) {
      query.set('sales_name', String(adminSalesName || '').trim());
    }
    const endpoint = adminMode
      ? `${api.list}?${query.toString()}`
      : `/api/tm/meeting-map?${query.toString()}`;
    const result = await apiClient.get(endpoint);
    setLoading(false);
    if (!result.ok) {
      showToast(result.data?.message || '미팅 목록 조회에 실패했습니다.', 'error');
      setMeetings([]);
      setSelectedId(null);
      setSheetOpen(false);
      return;
    }
    const rows = Array.isArray(result.data?.data) ? result.data.data : [];
    const sorted = [...rows].sort((a, b) =>
      String(a.visit_time || '').localeCompare(String(b.visit_time || ''))
    );
    setMeetings(sorted);
    setSelectedId((prev) =>
      (sorted.some((item) => Number(item.id) === Number(prev)) ? prev : null)
    );
    setSheetOpen(false);
  }

  // ── API: 결과 모달 참조 데이터 (인계 담당자 + 피드백 옵션) ───────────────────
  // 마운트 + 설정 모달 닫힘 + 결과 모달 열 때마다 재조회 — 관리자가 설정에서 바꾼 값이
  //   새로고침 없이 반영되게 한다 (2026-09-02, '미팅인지모름' 미반영 수정).
  function fetchMeetingRefs() {
    // 미팅인계 활성 대상자만 조회 (관리자 설정에서 비활성된 사람 제외)
    //   실패 시 콘솔 경고 (2026-09-01): 과거 403이 완전 무음이라 "빈 모달" 원인 추적이 불가능했음
    apiClient.get('/api/tm/meeting-inherit/sales-persons').then((r) => {
      if (r.ok) core.setSalesPersons(Array.isArray(r.data) ? r.data : (r.data?.data || []));
      else console.warn('[미팅지도] 인계 담당자 목록 조회 실패:', r.status, r.data?.message);
    });
    // 피드백 옵션 목록 (사용 빈도순 정렬, 활성 옵션만)
    apiClient.get('/api/tm/meeting-map/feedback-options').then((r) => {
      if (r.ok) core.setFeedbackOptions(Array.isArray(r.data) ? r.data : (r.data?.data || []));
      else console.warn('[미팅지도] 피드백 옵션 조회 실패:', r.status, r.data?.message);
    });
  }

  // ── API: 피드백 미입력 목록 조회 ────────────────────────────────────────────
  async function fetchPendingFeedback() {
    setPendingLoading(true);
    const endpoint = adminMode
      ? `${api.pending}${adminSalesName ? `?sales_name=${encodeURIComponent(adminSalesName)}` : ''}`
      : '/api/tm/meeting-map/pending-feedback';
    const result = await apiClient.get(endpoint);
    setPendingLoading(false);
    if (result.ok) {
      setPendingList(Array.isArray(result.data?.data) ? result.data.data : []);
    }
  }

  // ── API: 계약완료 상품 목록 조회 (최초 계약완료 토글 시 lazy fetch) ──────────
  // 캐시 히트 시 API 재호출 없이 카테고리명 치환만 재실행
  async function fetchContractTicketTypes() {
    if (contractTicketTypes.length > 0) {
      if (externalCategories.length > 0) {
        const extCats = externalCategories;
        const allProducts = contractTicketTypes;
        setResultForm(resolveExternalProducts(extCats, allProducts));
      }
      return;
    }
    const endpoint = adminMode
      ? api.ticketTypes
      : '/api/tm/contract-ticket-types';
    const res = await apiClient.get(endpoint);
    if (res.ok && Array.isArray(res.data?.data)) {
      setContractTicketTypes(res.data.data);
    }
    // 비내부 카테고리 정보 → selectedProducts 카테고리명을 기본상품으로 치환
    if (res.ok && Array.isArray(res.data?.external_categories)) {
      const extCats = res.data.external_categories;
      setExternalCategories(extCats);
      const allProducts = Array.isArray(res.data?.data) ? res.data.data : [];
      setResultForm(resolveExternalProducts(extCats, allProducts));
    }
  }

  // ── 헬퍼: default_validity_months가 설정된 상품인지 확인 ───────────────────
  function hasValiditySetting(name) {
    const found = contractTicketTypes.find((t) => t.name === name);
    return found?.default_validity_months != null;
  }

  // 당겨서-새로고침(pull-to-refresh)은 제거됨 — 지도 드래그 제스처와 충돌해 오작동.
  //   대신 헤더 🔄 버튼(fetchMeetings) 으로 명시적 새로고침. 마커 배치는 재조회 시 자동 복원.

  return {
    fetchMeetings,
    fetchPendingFeedback,
    fetchMeetingRefs,
    fetchContractTicketTypes,
    hasValiditySetting,
    extractValidity,
    parseContractTicketString,
    resetMarkerLayout,
  };
}
